import React, { useState, useEffect } from 'react';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, Percent, Info, CheckCircle2, Clock, PiggyBank, ArrowRight, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';

const CryptoRoiCalculatorTool = () => {
  const [initialInvestment, setInitialInvestment] = useState('');
  const [finalValue, setFinalValue] = useState('');
  const [duration, setDuration] = useState('');
  const [durationUnit, setDurationUnit] = useState('months');
  const [additionalContributions, setAdditionalContributions] = useState('');
  const [feeType, setFeeType] = useState('percent');
  const [feeAmount, setFeeAmount] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  // Real-time calculation
  useEffect(() => {
    calculate();
  }, [initialInvestment, finalValue, duration, durationUnit, additionalContributions, feeType, feeAmount]);

  const calculate = () => {
    const initial = parseFloat(initialInvestment);
    const final = parseFloat(finalValue);
    const dur = parseFloat(duration) || 0;
    const contributions = parseFloat(additionalContributions) || 0;
    const fee = parseFloat(feeAmount) || 0;

    if (isNaN(initial) || isNaN(final) || initial <= 0) {
      setResult(null);
      return;
    }

    const totalInvested = initial + contributions;
    
    let feeDeduction = 0;
    if (feeType === 'percent') {
      feeDeduction = final * (fee / 100);
    } else {
      feeDeduction = fee;
    }
    
    const netFinalValue = final - feeDeduction;
    const totalProfit = netFinalValue - totalInvested;
    const roi = ((netFinalValue - totalInvested) / totalInvested) * 100;
    const grossProfit = final - totalInvested;
    const grossRoi = ((final - totalInvested) / totalInvested) * 100;
    
    let durationInYears = dur;
    if (durationUnit === 'days') {
      durationInYears = dur / 365;
    } else if (durationUnit === 'months') {
      durationInYears = dur / 12;
    }
    
    let annualizedRoi = 0;
    if (durationInYears > 0 && totalInvested > 0 && netFinalValue > 0) {
      annualizedRoi = (Math.pow(netFinalValue / totalInvested, 1 / durationInYears) - 1) * 100;
    }
    
    const growthMultiplier = netFinalValue / totalInvested;
    const dailyReturn = durationInYears > 0 ? roi / (durationInYears * 365) : 0;
    const monthlyReturn = durationInYears > 0 ? roi / (durationInYears * 12) : 0;

    setResult({
      totalInvested, finalValue: final, netFinalValue, grossProfit, grossRoi,
      totalProfit, roi, annualizedRoi, feeDeduction, growthMultiplier,
      dailyReturn, monthlyReturn, durationInYears,
      isProfitable: totalProfit > 0, isBreakEven: Math.abs(totalProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setInitialInvestment(''); setFinalValue(''); setDuration('');
    setAdditionalContributions(''); setFeeAmount(''); setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: '2x Return', initial: '10000', final: '20000', dur: '12', unit: 'months', contrib: '', fee: '' },
    { label: '10x Gain', initial: '1000', final: '10000', dur: '2', unit: 'years', contrib: '', fee: '' },
    { label: 'With DCA', initial: '5000', final: '15000', dur: '12', unit: 'months', contrib: '3000', fee: '1' },
  ];

  const applyScenario = (scenario) => {
    setInitialInvestment(scenario.initial); setFinalValue(scenario.final);
    setDuration(scenario.dur); setDurationUnit(scenario.unit);
    setAdditionalContributions(scenario.contrib); setFeeAmount(scenario.fee);
  };

  const getRoiRating = (roi) => {
    if (roi >= 100) return { label: 'Exceptional', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (roi >= 50) return { label: 'Excellent', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (roi >= 20) return { label: 'Good', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (roi >= 0) return { label: 'Positive', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (roi >= -20) return { label: 'Minor Loss', color: 'text-orange-400', bg: 'bg-orange-500/20' };
    return { label: 'Significant Loss', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  return (
    <div className="space-y-6" data-testid="crypto-roi-calculator">
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button key={index} onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
            data-testid={`scenario-${index}`}>{scenario.label}</button>
        ))}
      </div>

      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <PiggyBank size={18} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">Investment Details</h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Initial Investment ($) <span className="text-red-400">*</span></label>
            <input type="number" step="0.01" min="0" value={initialInvestment} onChange={(e) => setInitialInvestment(e.target.value)}
              placeholder="e.g., 10000" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none" data-testid="initial-investment-input" />
            <p className="text-xs text-gray-500 mt-1">Amount you initially invested</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Current/Final Value ($) <span className="text-red-400">*</span></label>
            <input type="number" step="0.01" min="0" value={finalValue} onChange={(e) => setFinalValue(e.target.value)}
              placeholder="e.g., 25000" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none" data-testid="final-value-input" />
            <p className="text-xs text-gray-500 mt-1">Current portfolio value or exit value</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Investment Duration</label>
            <div className="flex gap-2">
              <input type="number" step="1" min="0" value={duration} onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g., 12" className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none" data-testid="duration-input" />
              <select value={durationUnit} onChange={(e) => setDurationUnit(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none" data-testid="duration-unit-select">
                <option value="days">Days</option>
                <option value="months">Months</option>
                <option value="years">Years</option>
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">For annualized ROI calculation</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Additional Contributions ($) <span className="text-gray-500">(optional)</span></label>
            <input type="number" step="0.01" min="0" value={additionalContributions} onChange={(e) => setAdditionalContributions(e.target.value)}
              placeholder="e.g., 2000" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none" data-testid="contributions-input" />
            <p className="text-xs text-gray-500 mt-1">DCA or additional deposits</p>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-4 mt-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">Fees <span className="text-gray-500">(optional)</span></label>
          <div className="flex gap-2">
            <select value={feeType} onChange={(e) => setFeeType(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-blue-500 focus:outline-none" data-testid="fee-type-select">
              <option value="percent">% of Value</option>
              <option value="fixed">Fixed ($)</option>
            </select>
            <input type="number" step="0.01" min="0" value={feeAmount} onChange={(e) => setFeeAmount(e.target.value)}
              placeholder={feeType === 'percent' ? 'e.g., 1' : 'e.g., 100'} className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none" data-testid="fee-input" />
          </div>
          <p className="text-xs text-gray-500 mt-1">Exchange fees, withdrawal fees, etc.</p>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={clearAll} className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors" data-testid="clear-btn">
            <Trash2 size={16} /> Clear All
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-lg border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/50' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {result.isProfitable ? <TrendingUp className="text-green-400" size={28} /> : result.isBreakEven ? <DollarSign className="text-yellow-400" size={28} /> : <TrendingDown className="text-red-400" size={28} />}
                <span className="text-lg font-medium text-gray-200">Return on Investment (ROI)</span>
              </div>
              <button onClick={() => copyResult(result.roi, 'roi')} className="p-2 hover:bg-gray-700 rounded transition-colors">
                {copied === 'roi' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>{formatPercent(result.roi)}</p>
              <div className="mt-2">
                <span className={`inline-block px-3 py-1 rounded-full text-sm ${getRoiRating(result.roi).bg} ${getRoiRating(result.roi).color}`}>{getRoiRating(result.roi).label}</span>
              </div>
              <p className="text-gray-400 mt-2">Your investment grew <span className="text-white font-semibold">{result.growthMultiplier.toFixed(2)}x</span></p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Profit</p>
                  <p className={`text-2xl font-bold ${result.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{result.totalProfit >= 0 ? '+' : ''}{formatCurrency(result.totalProfit)}</p>
                </div>
                <button onClick={() => copyResult(result.totalProfit, 'profit')} className="p-1 hover:bg-gray-700 rounded transition-colors">
                  {copied === 'profit' ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Net profit after fees</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Annualized ROI</p>
                  <p className={`text-2xl font-bold ${result.annualizedRoi >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>{result.durationInYears > 0 ? formatPercent(result.annualizedRoi) : 'N/A'}</p>
                </div>
                <Clock size={20} className="text-gray-500" />
              </div>
              <p className="text-xs text-gray-500 mt-1">Yearly return rate (CAGR)</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400">Total Invested</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(result.totalInvested)}</p>
              <p className="text-xs text-gray-500 mt-1">Initial + contributions</p>
            </div>

            <div className="p-4 bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-400">Net Final Value</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(result.netFinalValue)}</p>
              <p className="text-xs text-gray-500 mt-1">After fees deducted</p>
            </div>
          </div>

          {result.feeDeduction > 0 && (
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Percent size={16} className="text-orange-400" />
                <h4 className="text-sm font-medium text-gray-300">Fee Impact</h4>
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div><p className="text-gray-400">Gross ROI (before fees)</p><p className="text-white font-medium">{formatPercent(result.grossRoi)}</p></div>
                <div><p className="text-gray-400">Fees Deducted</p><p className="text-orange-400 font-medium">-{formatCurrency(result.feeDeduction)}</p></div>
                <div><p className="text-gray-400">Net ROI (after fees)</p><p className={`font-medium ${result.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatPercent(result.roi)}</p></div>
              </div>
            </div>
          )}

          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={16} className="text-purple-400" />
              <h4 className="text-sm font-medium text-gray-300">Investment Growth Summary</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-gray-400">Starting Investment</span>
                <span className="text-white">{formatCurrency(parseFloat(initialInvestment) || 0)}</span>
              </div>
              {parseFloat(additionalContributions) > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">+ Additional Contributions</span>
                  <span className="text-blue-400">+{formatCurrency(parseFloat(additionalContributions))}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-gray-400">= Total Invested</span>
                <span className="text-white font-medium">{formatCurrency(result.totalInvested)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-700">
                <span className="text-gray-400">Final Portfolio Value</span>
                <span className="text-white">{formatCurrency(result.finalValue)}</span>
              </div>
              {result.feeDeduction > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">- Fees</span>
                  <span className="text-orange-400">-{formatCurrency(result.feeDeduction)}</span>
                </div>
              )}
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-200 font-medium">Net Profit/Loss</span>
                <span className={`font-bold text-lg ${result.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{result.totalProfit >= 0 ? '+' : ''}{formatCurrency(result.totalProfit)}</span>
              </div>
            </div>
          </div>

          {result.durationInYears > 0 && (
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Average Returns by Period</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><p className="text-gray-400 text-xs mb-1">Daily</p><p className={`font-semibold ${result.dailyReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>{result.dailyReturn >= 0 ? '+' : ''}{result.dailyReturn.toFixed(3)}%</p></div>
                <div><p className="text-gray-400 text-xs mb-1">Monthly</p><p className={`font-semibold ${result.monthlyReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>{result.monthlyReturn >= 0 ? '+' : ''}{result.monthlyReturn.toFixed(2)}%</p></div>
                <div><p className="text-gray-400 text-xs mb-1">Yearly (CAGR)</p><p className={`font-semibold ${result.annualizedRoi >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>{result.annualizedRoi >= 0 ? '+' : ''}{result.annualizedRoi.toFixed(2)}%</p></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Affiliate Block - Show after results */}
      {result && <CryptoAffiliateBlock 
        profit={result.totalProfit} 
        cryptoName="crypto"
        toolName="Crypto ROI Calculator"
        toolSlug="crypto-roi-calculator"
      />}

      <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-3">Complete Your Crypto Analysis</h4>
        <div className="flex flex-wrap gap-3">
          <Link to="/tools/crypto-profit-calculator" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors">
            <Calculator size={16} className="text-green-400" /> Calculate your crypto profit first <ArrowRight size={14} />
          </Link>
          <Link to="/tools/crypto-tax-calculator" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors">
            <Percent size={16} className="text-orange-400" /> Estimate your crypto taxes <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-purple-400" />
          <h4 className="text-sm font-medium text-gray-300">ROI Formulas</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div><p className="text-gray-400">Basic ROI:</p><code className="text-purple-400">((Final - Initial) / Initial) × 100</code></div>
          <div><p className="text-gray-400">Annualized ROI (CAGR):</p><code className="text-purple-400">(Final/Initial)^(1/years) - 1</code></div>
          <div><p className="text-gray-400">Net ROI:</p><code className="text-purple-400">ROI after deducting fees</code></div>
          <div><p className="text-gray-400">Growth Multiplier:</p><code className="text-purple-400">Final Value / Initial Investment</code></div>
        </div>
      </div>

      {/* SEO Content Section */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        
        {/* What is Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Crypto ROI Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Crypto ROI Calculator</strong> is a powerful investment analysis tool that measures your 
              return on investment from cryptocurrency holdings. ROI (Return on Investment) expresses your profit 
              or loss as a percentage of your initial investment, making it easy to evaluate and compare different 
              investment opportunities.
            </p>
            <p>
              Our calculator goes beyond basic ROI by providing <strong>annualized ROI (CAGR)</strong> — the Compound 
              Annual Growth Rate that normalizes returns across different time periods. This is crucial for comparing 
              a 6-month trade to a 2-year hold, or evaluating whether your crypto investments are outperforming 
              traditional assets like stocks or bonds.
            </p>
            <p>
              The calculator also shows your <strong>growth multiplier</strong> (how many times your money grew), 
              <strong>total profit</strong> in dollar terms, and supports <strong>additional contributions</strong> 
              for those who dollar-cost average (DCA) into their positions. Whether you're a day trader or long-term 
              holder, understanding your true ROI is essential for making data-driven investment decisions.
            </p>
          </div>
        </section>

        {/* How to Use Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Crypto ROI Calculator</h2>
          <div className="space-y-3">
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold flex-shrink-0">1</div>
              <div>
                <h3 className="font-semibold text-white">Enter Initial Investment</h3>
                <p className="text-gray-400 text-sm">Input the total amount you originally invested in cryptocurrency. This is your starting capital or cost basis.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold flex-shrink-0">2</div>
              <div>
                <h3 className="font-semibold text-white">Enter Current/Final Value</h3>
                <p className="text-gray-400 text-sm">Input the current value of your holdings or the value when you sold. This determines your ending position.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold flex-shrink-0">3</div>
              <div>
                <h3 className="font-semibold text-white">Select Investment Duration</h3>
                <p className="text-gray-400 text-sm">Choose the time period (days, months, or years) to calculate annualized ROI (CAGR) for accurate performance comparison.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold flex-shrink-0">4</div>
              <div>
                <h3 className="font-semibold text-white">Add Contributions (Optional)</h3>
                <p className="text-gray-400 text-sm">If you added funds over time (DCA strategy), include additional contributions for accurate total investment calculation.</p>
              </div>
            </div>
            <div className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center font-bold flex-shrink-0">5</div>
              <div>
                <h3 className="font-semibold text-white">View Comprehensive Results</h3>
                <p className="text-gray-400 text-sm">See your ROI percentage, annualized ROI, growth multiplier, total profit, and period-by-period return breakdown.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Example Calculation Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example ROI Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: 2-Year Crypto Investment</h3>
            <div className="space-y-3 text-gray-300">
              <p>Let's calculate the ROI for a long-term crypto investment:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Initial Investment:</strong> $10,000</li>
                <li><strong>Current Value:</strong> $35,000</li>
                <li><strong>Duration:</strong> 2 years</li>
                <li><strong>Additional Contributions:</strong> $2,000</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg">
                <p className="font-mono text-sm">
                  <span className="text-gray-400">Total Invested:</span> <span className="text-white">$10,000 + $2,000 = $12,000</span><br/>
                  <span className="text-gray-400">Total Profit:</span> <span className="text-green-400">$35,000 - $12,000 = $23,000</span><br/>
                  <span className="text-gray-400">Basic ROI:</span> <span className="text-blue-400">($23,000 / $12,000) × 100 = 191.67%</span><br/>
                  <span className="text-gray-400">Growth Multiplier:</span> <span className="text-purple-400">$35,000 / $12,000 = 2.92x</span><br/>
                  <span className="text-gray-400">Annualized ROI (CAGR):</span> <span className="text-cyan-400">(35000/12000)^(1/2) - 1 = 70.78%</span>
                </p>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                Your $12,000 total investment grew to $35,000, yielding a 191.67% ROI (2.92x). The annualized return of 70.78% 
                means your investment grew at an average rate of 70.78% per year — significantly outperforming traditional markets.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">What's the difference between ROI and CAGR?</h3>
              <p className="text-gray-400 text-sm">
                ROI (Return on Investment) shows your total percentage return regardless of time. CAGR (Compound Annual Growth Rate) 
                is the annualized return rate, showing what your investment grew by each year on average. A 100% ROI over 4 years 
                equals roughly 19% CAGR, while 100% ROI in 1 year equals 100% CAGR.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">What is a growth multiplier?</h3>
              <p className="text-gray-400 text-sm">
                Growth multiplier shows how many times your investment grew. A 2x multiplier means you doubled your money (100% ROI). 
                A 10x multiplier means your investment grew 10 times (900% ROI). It's a quick way to visualize investment performance.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">How do I calculate ROI with multiple purchases?</h3>
              <p className="text-gray-400 text-sm">
                Add all your purchases together as "Total Invested" (including DCA contributions). Your ROI is then 
                (Current Value - Total Invested) / Total Invested × 100. For precise tracking, use the "Additional Contributions" 
                field in our calculator.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">What's a good crypto ROI?</h3>
              <p className="text-gray-400 text-sm">
                Bitcoin has historically averaged 100-200% annual returns since 2010, but with extreme volatility. 
                A "good" ROI depends on your risk tolerance and time horizon. Any positive ROI that beats inflation and 
                matches your risk profile is generally considered good. However, past performance doesn't guarantee future returns.
              </p>
            </div>
            <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <h3 className="font-semibold text-white mb-2">Should I include unrealized gains in ROI calculation?</h3>
              <p className="text-gray-400 text-sm">
                Yes, you can calculate ROI on unrealized (paper) gains using current market value. However, remember that 
                unrealized gains can disappear if the market drops. For tax purposes, only realized gains (when you actually sell) 
                are typically taxable.
              </p>
            </div>
          </div>
        </section>
      </div>

      <div className="p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-lg border border-green-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2">💡 ROI Tips for Crypto Investors</h4>
        <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
          <li>Always calculate ROI after fees — exchange and gas fees add up quickly</li>
          <li>Use annualized ROI (CAGR) to compare investments of different durations</li>
          <li>A 100% ROI means you doubled your money (2x return)</li>
          <li>Past ROI doesn't guarantee future returns — crypto is highly volatile</li>
          <li>Track your cost basis accurately for tax purposes</li>
        </ul>
      </div>
    </div>
  );
};

export default CryptoRoiCalculatorTool;
